import { attr } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import _sortBy from 'lodash/sortBy';
import BaseModel from 'volta/models/base-model';

import type { IResourceName } from 'volta/models/base-model';
import type { IQuantityUnit } from './types/planning-order';
import type { IBufferInfo } from './types/stats';

export const POGroupResourceName: IResourceName = {
  singular: 'poGroup',
  plural: 'poGroups'
};

export type TPOField =
  | 'abcClass'
  | 'acqCode'
  | 'cancelledAt'
  | 'committedAt'
  | 'createdBy'
  | 'currency'
  | 'customFields'
  | 'deliveryDate'
  | 'deliveryWorkshop'
  | 'dueDate'
  | 'estimatedEndDate'
  | 'estimatedEndTime'
  | 'estimatedProcessTime'
  | 'estimatedStartTime'
  | 'executionBuffer'
  | 'externalOrderNum'
  | 'finishedAt'
  | 'g'
  | 'groupId'
  | 'id'
  | 'isOperation'
  | 'launchDate'
  | 'launchedAt'
  | 'leadTimeCategory'
  | 'methodology'
  | 'nextOperationNum'
  | 'operationCustomFields'
  | 'operationName'
  | 'operationNum'
  | 'operationStarted'
  | 'orderNum'
  | 'orderType'
  | 'parentPoId'
  | 'part'
  | 'partType'
  | 'planningBuffer'
  | 'planningId'
  | 'priority'
  | 'processCompletion'
  | 'processPcb'
  | 'processTime'
  | 'reorderDate'
  | 'reorderQty'
  | 'reorderStrategy'
  | 'resourceQty'
  | 'routeType'
  | 'schedulingAlert'
  | 'sentAt'
  | 'setupPcb'
  | 'setupTime'
  | 'sku'
  | 'sku.customFields'
  | 'startedAt'
  | 'status'
  | 'supplier'
  | 'syncAlert'
  | 'syncStatus'
  | 'timeBufferAlert'
  | 'totalPrice'
  | 'unitPrice'
  | 'validationStatus'
  | 'varCategory'
  | 'warehouse'
  | 'workshop'
  | 'xyzClass';

export type TPOGroupFields = {
  key: TPOField;
  id?: string;
  value?: string;
  desc?: string;
};

export type TPOGroupFieldsKey = Partial<Record<TPOField, TPOGroupFields>> & { id: string };

export default class POGroup extends BaseModel {
  @attr({ defaultValue: () => [] }) fields!: TPOGroupFields[];
  @attr() reorderQty!: Record<string, number>;
  @attr() reorderQtyValue!: Record<string, number>;
  @attr('number') totalCount!: number;
  @attr('number') filteredCount!: number;
  @attr('number') comments!: number;
  @attr('number') schedulingAlerts!: number;
  @attr('number') syncAlerts!: number;
  @attr('number') timeBufferAlerts!: number;
  @attr() planningBuffer!: IBufferInfo<number>;
  @attr() executionBuffer!: IBufferInfo<number>;
  @attr('array') unitMeasures!: string[];

  get planningBufferDisplay() {
    return toBufferPercent(this.planningBuffer);
  }

  get executionBufferDisplay() {
    return toBufferPercent(this.executionBuffer);
  }

  get groupBy() {
    return this.fields.map((f) => f.key);
  }

  get workshopId() {
    return this.fields.find((f) => f.key === 'workshop')?.id;
  }

  get keyObject() {
    const o: TPOGroupFieldsKey = { id: this.id } as TPOGroupFieldsKey;
    this.fields.forEach((f) => {
      o[f.key] = f as string & TPOGroupFields;
    });
    return o;
  }

  get alertsCount() {
    return this.syncAlerts + this.schedulingAlerts + this.timeBufferAlerts;
  }

  get counts() {
    return this.alertsCount + this.comments;
  }

  get reorderQtyValueOptions() {
    return _sortBy(
      Object.entries(this.reorderQtyValue).map(([currency, value]) => {
        return {
          currency,
          value
        };
      }),
      'currency'
    );
  }

  get convertedQtiesOptions(): IQuantityUnit[] {
    return _sortBy(
      Object.entries(this.reorderQty).map(([unit, value]) => {
        return {
          unit,
          value
        };
      }),
      'value'
    );
  }

  [Type] = 'po-group' as const;
}

const toBufferPercent = (bufferInfo: IBufferInfo<number>) => {
  const { critical, high, medium, low, otog } = bufferInfo;
  const total = critical + high + medium + low + otog;
  return {
    critical: total === 0 ? 0 : critical / total,
    high: total === 0 ? 0 : high / total,
    medium: total === 0 ? 0 : medium / total,
    low: total === 0 ? 0 : low / total,
    otog: total === 0 ? 0 : otog / total
  };
};
